import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GenericValidators } from 'src/app/shared/generic-validators';
import { VehicleDevicesService } from './vehicle-devices.service';
import { MaintainVehicle } from 'src/app/models/constant/right';
import { count, filter, forkJoin, map, of, switchMap, take, takeUntil, takeWhile, tap, timer } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { DynamicDialogService } from 'src/app/dynamic-dialog/dynamic-dialog.service';
import { Vehicle } from 'src/app/models/vehicle';
import { TelematicVehicle } from 'src/app/models/telematicVehicle';
import { ToastGlobalService } from 'src/app/layout/toast-global/toast-global.service';

@Component({
    selector: 'app-vehicle-devices',
    templateUrl: './vehicle-devices.component.html',
    styleUrls: ['./vehicle-devices.component.css'],
    standalone: false
})
export class VehicleDevicesComponent {
  telematicDeviceDisplayColumns = [
    {
      display: '@provider',
      property: 'telematicProviderType',
      type: 'text',
      noSort: true
    },
    {
      display: '@deviceId',
      property: 'telematicDeviceId',
      type: 'text',
      noSort: true
    },
    {
      display: '@serialNumber',
      property: 'serialNumber',
      type: 'text',
      noSort: true
    },
    {
      display: '@active',
      property: 'isActive',
      type: 'boolean',
      noSort: true
    },
    {
      display: '@name',
      property: 'name',
      type: 'text',
      noSort: true
    },
    {
      display: '@updateDate',
      property: 'updateDateTimeUtc',
      type: 'date',
      noSort: true
    }
  ];
  telematicDeviceFunctions = [
    {
      display: '@toggleDevice',
      action: 'toggle',
      icon: 'fa fa-sliders',
      validator: (rows) => this.inToggleProviders(rows), 
      requireRight: () => this.validators.hasRight(MaintainVehicle),
      clearSelectedItems: true
    },
    {
      display: '@displayInactiveDevices',
      action: 'showInactive',
      icon: 'fa fa-moon',
      requireRight: () => this.validators.hasRight(MaintainVehicle),
    },
  ];
  
  telematicVehicleTableData = new MatTableDataSource<TelematicVehicle>();
  toggleProviders: any[];
  createProviders: any[];
  showInactive: boolean = false;
  getSecondDelay: number = 2;
  
  @Input() vehicle: Vehicle;
  telamaticVehicles: TelematicVehicle[] = [];

  constructor(private validators: GenericValidators,
    private translate: TranslateService,
    private vehicleDeviceService: VehicleDevicesService,
    private dialogService: DynamicDialogService,
    private toastGlobalService: ToastGlobalService) {

  }
  ngOnInit(): void{
    this.getProviders();
    this.getDevices();
  }
  getDevices(): void {
    this.vehicleDeviceService
      .getTelematicVehicle(this.vehicle.vin, this.showInactive)
      .pipe(
        map(
          telematicVehicles => {
            if(this.telamaticVehicles?.length === telematicVehicles.length && this.showInactive){
              const toast = this.translate.instant('@noInactiveDevicesFound');
              this.toastGlobalService.show(toast, { classname: 'warning' });
            }
            this.setTableData(telematicVehicles);
          }))
      .subscribe();
  }
  inToggleProviders(rows: any[]): string {
    var output = '';
    rows.forEach(row => {
      if(!this.toggleProviders.find(x => x.telematicProviderId === row.telematicProviderId)){
        output += this.translate.instant("@toggleNotSupportedMessage") + '\n';
      }
    });
    output += this.validators.selectedLengthValidator(rows, '=', 1);
    return output;
  }
  getProviders(): void {
    forkJoin([this.vehicleDeviceService.getToggleProviders(), this.vehicleDeviceService.getCreateProviders()])
    .subscribe(([toggleProviders, createProviders]) => {
      this.toggleProviders = toggleProviders;
      this.createProviders = createProviders;
      if(this.createProviders.length > 0 && !this.telematicDeviceFunctions.some(s => s.action === 'add')) {
        this.telematicDeviceFunctions = [
          {
            display: '@addNewDevice',
            action: 'add',
            icon: 'fa fa-plus',
            validator: (rows) => this.validators.notSelectedValidator(rows),
            requireRight: () => this.validators.hasRight(MaintainVehicle)
          } as any,
          ...this.telematicDeviceFunctions];
      }
    });
  }
  getDevicesUntilDeviceAdded(){
    timer(0, this.getSecondDelay * 1000).pipe(
      take(3),
      switchMap(() => this.vehicleDeviceService.getTelematicVehicle(this.vehicle.vin, this.showInactive)),
      filter((telamaticVehicles: TelematicVehicle[]) => telamaticVehicles.length !== this.telamaticVehicles.length),
      takeWhile((telamaticVehicles: TelematicVehicle[]) => !(telamaticVehicles.length !== this.telamaticVehicles.length), true),
      map((telamaticVehicles: TelematicVehicle[]) => {
        this.setTableData(telamaticVehicles);
      }))
      .subscribe();    
  }
  
  getDevicesUntilDeviceUpdated(telematicVehicle: TelematicVehicle) {
    timer(0, this.getSecondDelay * 1000).pipe(
      take(3),
      switchMap(() => this.vehicleDeviceService.getTelematicVehicle(this.vehicle.vin, this.showInactive)),
      filter((telamaticVehicles: TelematicVehicle[]) => 
        this.deviceUpdated(telamaticVehicles, telematicVehicle) 
        || telamaticVehicles.length !== this.telamaticVehicles.length),
      takeWhile((telamaticVehicles: TelematicVehicle[]) => 
        !this.deviceUpdated(telamaticVehicles, telematicVehicle) 
        || !(telamaticVehicles.length !== this.telamaticVehicles.length), true),
      map((telamaticVehicles: TelematicVehicle[]) => {
        this.setTableData(telamaticVehicles);
      }))
      .subscribe()    
  }
  setTableData(telamaticVehicles: TelematicVehicle[]){
    this.telamaticVehicles = telamaticVehicles;
    this.telematicVehicleTableData = new MatTableDataSource<TelematicVehicle>(telamaticVehicles);
  }
  deviceUpdated(devices: TelematicVehicle[], deviceToggled: TelematicVehicle): boolean {
    var foundDevice = 
      devices.find(x => 
        x.telematicDeviceId === deviceToggled.telematicDeviceId
        && x.telematicProviderId === deviceToggled.telematicProviderId
        && x.isActive === !deviceToggled.isActive);

     if(foundDevice){
      return true;
     }
    return false;
  }
  processTelematicDeviceFunction($event: any): void {
    switch($event.action){
      case 'add':
        const $dialog = this.dialogService.openNewTelematicVehicleDialog(this.vehicle, this.createProviders).afterClosed();
        $dialog
        .pipe(
          filter((response: any) => response),
          tap((response: TelematicVehicle) => {
            response.vin = this.vehicle.vin;
            response.telematicProviderId = response.telematicProviderId;
          }),
          switchMap((response: TelematicVehicle) => this.vehicleDeviceService.addTelematicVehicle(response)))
          .subscribe(() => {            
            const toast = this.translate.instant('@updateSuccessful');
            this.toastGlobalService.show(toast, { classname: 'success' });
            this.getDevicesUntilDeviceAdded();
          });
        break;
      case 'toggle':
        const message = $event.rowsSelected[0].isActive ? '@confirmDeviceToggleInactiveMessage': '@confirmDeviceToggleActiveMessage';
        const $confirmDialog = this.dialogService.openDynamicConfirmDialog({ message: message }).afterClosed();
        $confirmDialog
        .pipe(
          filter((agreed: boolean) => agreed),
          switchMap(() => this.vehicleDeviceService.toggleTelematicVehicle($event.rowsSelected[0])))
        .subscribe(() => {
          const toast = this.translate.instant('@updateSuccessful');
          this.toastGlobalService.show(toast, { classname: 'success' });
          this.getDevicesUntilDeviceUpdated($event.rowsSelected[0]);
        });
        break;
      case 'showInactive':
        this.showInactive = !this.showInactive;
        this.getDevices();
        break;
    }
  }
}
